"use client";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RESERVED_DISCOVER_SEGMENTS } from "@/constants/reservedSlugs";

const STORAGE_KEY = "discover-search-state";

export function useSearchStateManager(locale: string) {
  const params = useSearchParams();
  const pathname = usePathname();

  // sessionStorage is unavailable during SSR. Reading it while deriving
  // `homeLink` would make the first client render diverge from the server
  // HTML (hydration mismatch), so defer the read until after mount: the first
  // render uses the locale-root fallback (matching the server), then upgrades.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isHomePage = pathname === `/${locale}`;
  const pathParts = pathname.split("/").filter(Boolean);
  const routeLocale = pathParts[0];
  const routeLocation = pathParts[1];
  const isListingPage =
    routeLocale === locale &&
    Boolean(routeLocation) &&
    !RESERVED_DISCOVER_SEGMENTS.has(routeLocation);

  const storeSearchState = useCallback(() => {
    if (!isHomePage && !isListingPage) return;

    const currentParams = new URLSearchParams(params);
    const paramsString = currentParams.toString();
    const basePath = isListingPage ? pathname : `/${locale}`;
    const nextState = paramsString ? `${basePath}?${paramsString}` : basePath;

    sessionStorage.setItem(STORAGE_KEY, nextState);
  }, [isHomePage, isListingPage, locale, params, pathname]);

  const getStoredSearchState = useCallback(() => {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem(STORAGE_KEY);
  }, []);

  useEffect(() => {
    if (isHomePage || isListingPage) {
      storeSearchState();
    }
  }, [isHomePage, isListingPage, storeSearchState]);

  const isValidStoredState = useCallback(
    (storedState: string) =>
      storedState === `/${locale}` ||
      storedState.startsWith(`/${locale}/`) ||
      storedState.startsWith(`/${locale}?`),
    [locale],
  );

  // Computed during render: no redundant state or Effect
  const homeLink = useMemo(() => {
    if (isHomePage) {
      return `/${locale}`;
    }

    if (isListingPage) {
      const currentParams = new URLSearchParams(params);
      const paramsString = currentParams.toString();
      return paramsString ? `${pathname}?${paramsString}` : pathname;
    }

    // Only the locale root is hydration-safe before mount (see isMounted note).
    if (!isMounted) {
      return `/${locale}`;
    }

    const storedState = getStoredSearchState();
    if (storedState && isValidStoredState(storedState)) {
      return storedState;
    }

    return `/${locale}`;
  }, [
    locale,
    isHomePage,
    isListingPage,
    params,
    pathname,
    isMounted,
    getStoredSearchState,
    isValidStoredState,
  ]);

  return homeLink;
}
